import React, { useState, useEffect } from "react";
import API_BASE_URL from "./config/api";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import styled, { keyframes } from "styled-components";

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

const PageWrapper = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: "Poppins", sans-serif;
`;

const HeroSection = styled.div`
  position: relative;
  height: 400px;
  background-color: #02495b;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(2, 73, 91, 0.95), rgba(28, 168, 203, 0.8));
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-family: "Merriweather", serif;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  font-weight: 300;
`;

const MainContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 80px;
`;

// Intro Section (Split Layout)
const IntroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 100px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const IntroText = styled.div`
  animation: ${fadeIn} 0.8s ease-out;
`;

const SectionLabel = styled.span`
  color: #1CA8CB;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  display: block;
  margin-bottom: 10px;
`;

const SectionTitle = styled.h2`
  font-family: "Merriweather", serif;
  font-size: 2.5rem;
  color: #004e61;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  color: #555;
  line-height: 1.8;
  margin-bottom: 30px;
  font-size: 1.05rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  gap: 15px;
`;

const CheckIcon = styled.div`
  min-width: 24px;
  height: 24px;
  background: #e0f7fa;
  color: #1CA8CB;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-top: 3px;
  font-weight: bold;
`;

const IntroImageWrapper = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  transform: rotate(2deg);
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(0deg);
  }
`;

const IntroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

// Openings Section
const OpeningsSection = styled.section`
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const JobCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: left;
  border: 1px solid #edf2f7;
  box-shadow: 0 10px 30px rgba(0,0,0,0.03);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 78, 97, 0.1);
    border-color: rgba(28, 168, 203, 0.2);
  }
`;

const JobCardTitle = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 1.4rem;
  color: #004e61;
  margin-bottom: 15px;
  font-weight: 700;
`;

const JobDesc = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 25px;
  flex-grow: 1;
`;

const ApplyBtn = styled.button`
  background: transparent;
  color: #1CA8CB;
  border: 2px solid #1CA8CB;
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: #1CA8CB;
    color: white;
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: white;
  width: 100%;
  max-width: 550px;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  
  &:hover { color: #333; }
`;

const ModalHeader = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 1.8rem;
  color: #004e61;
  margin-bottom: 30px;
  text-align: center;
`;

// Form Inputs (Reusing clean styles)
const StyledInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 16px;
  background: #f8fafc;
  transition: all 0.2s;
  margin-bottom: 15px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1CA8CB;
    background: white;
    box-shadow: 0 0 0 3px rgba(28, 168, 203, 0.1);
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 16px;
  background: #f8fafc;
  min-height: 120px;
  resize: vertical;
  transition: all 0.2s;
  margin-bottom: 20px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #1CA8CB;
    background: white;
    box-shadow: 0 0 0 3px rgba(28, 168, 203, 0.1);
  }
`;

const SubmitBtn = styled.button`
  background: #02495b;
  color: white;
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #03627c;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// ----------------------
// Career Component
// ----------------------
const Career = () => {
  const [pageData, setPageData] = useState({
    title: "Careers",
    mainContent: {
      heading: "Join Our Team",
      subheading: "",
      description:
        "At Trippro, we believe in fostering talent and empowering individuals to achieve their best. Explore exciting opportunities to grow with us.",
      bulletPoints: [
        {
          title: "Innovative Environment",
          description:
            "Work with cutting-edge technology and creative minds to shape the future of travel.",
        },
        {
          title: "Growth Opportunities",
          description:
            "Advance your career with professional development and leadership programs.",
        },
      ],
      careerOptions: [
        { id: 1, title: "Co-founder", description: "Shape the long-term vision and drive the overall strategic evolution of Trippro from the ground up." },
        { id: 2, title: "CTO", description: "Architect our technology roadmap and lead the engineering team in building scalable, world-class travel solutions." },
        { id: 3, title: "CFO", description: "Drive financial strategy, manage capital allocation, and ensure the long-term fiscal health of the company." },
        { id: 4, title: "Marketing Head", description: "Define our brand identity and lead high-impact growth strategies to make Trippro a household name." },
      ],
    },
    images: [
      {
        src: "https://img.freepik.com/free-vector/team-leader-teamwork-concept_74855-6671.jpg?uid=R180763786&ga=GA1.1.1046031348.1735292585&semt=ais_hybrid",
        alt: "Career Image",
      },
    ],
  });

  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Simulate API fetch or use real one
    fetch(`${API_BASE_URL}/careers-data`) // Assuming this endpoint exists or will fail gracefully to default state
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Failed to fetch");
      })
      .then(setPageData)
      .catch((err) => console.log("Using default data", err));
  }, []);

  const handleApplyNowClick = (job) => {
    setSelectedJob(job);
    setFormModalOpen(true);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const formdata = new FormData();
    formdata.append("name", e.target.name.value);
    formdata.append("email", e.target.email.value);
    formdata.append("phoneNumber", e.target.phone.value);
    // formdata.append("jobId", selectedJob.id); // If backend supports it
    formdata.append("message", e.target.message.value);

    if (file) {
      formdata.append("uploadFile", file);
    }

    fetch(`${API_BASE_URL}/contact`, {
      method: "POST",
      body: formdata,
      redirect: "follow",
    })
      .then((response) => response.text())
      .then((result) => {
        console.log(result);
        alert(`Application for ${selectedJob.title} submitted successfully!`);
        setFormModalOpen(false);
        setLoading(false);
        e.target.reset();
        setFile(null);
      })
      .catch((error) => {
        console.error(error);
        alert("Something went wrong. Please try again.");
        setLoading(false);
      });
  };

  return (
    <>
      <Navbar />
      <PageWrapper>
        {/* Hero */}
        <HeroSection>
          <HeroOverlay />
          <HeroContent>
            <HeroTitle>Careers at Trippro</HeroTitle>
            <HeroSubtitle>Shape the future of intelligent travel with us.</HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <MainContainer>
          {/* Intro Section */}
          <IntroSection>
            <IntroText>
              <SectionLabel>Why Join Us</SectionLabel>
              <SectionTitle>{pageData.mainContent.heading}</SectionTitle>
              <Description>{pageData.mainContent.description}</Description>

              <FeatureList>
                {pageData.mainContent.bulletPoints.map((point, i) => (
                  <FeatureItem key={i}>
                    <CheckIcon>✓</CheckIcon>
                    <div>
                      <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                        {point.title}
                      </h4>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem', lineHeight: '1.5' }}>
                        {point.description}
                      </p>
                    </div>
                  </FeatureItem>
                ))}
              </FeatureList>
            </IntroText>

            <IntroImageWrapper>
              <IntroImage src={pageData.images[0]?.src} alt="Team" />
            </IntroImageWrapper>
          </IntroSection>

          {/* Openings Section */}
          <OpeningsSection>
            <SectionLabel>Join Our Team</SectionLabel>
            <SectionTitle>Current Openings</SectionTitle>
            <Description style={{ maxWidth: "600px", margin: "0 auto" }}>
              Find the role that fits your skills and passion.
            </Description>

            <Grid>
              {pageData.mainContent.careerOptions.map((job) => (
                <JobCard key={job.id}>
                  <JobCardTitle>{job.title}</JobCardTitle>
                  <JobDesc>{job.description}</JobDesc>
                  <ApplyBtn onClick={() => handleApplyNowClick(job)}>
                    Apply Now
                  </ApplyBtn>
                </JobCard>
              ))}
            </Grid>
          </OpeningsSection>
        </MainContainer>

        {/* Modal */}
        {isFormModalOpen && (
          <ModalOverlay onClick={() => setFormModalOpen(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
              <CloseBtn onClick={() => setFormModalOpen(false)}>×</CloseBtn>
              <ModalHeader>Apply for {selectedJob?.title}</ModalHeader>

              <form onSubmit={handleSubmit}>
                <StyledInput type="text" name="name" placeholder="Full Name" required />
                <StyledInput type="email" name="email" placeholder="Email Address" required />
                <StyledInput type="tel" name="phone" placeholder="Phone Number" required />

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555', fontWeight: '500' }}>
                    Resume / CV
                  </label>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{ fontSize: '14px' }} />
                </div>

                <StyledTextarea name="message" placeholder="Why do you want to join us?" required></StyledTextarea>

                <SubmitBtn type="submit" disabled={loading}>
                  {loading ? "Submitting..." : "Submit Application"}
                </SubmitBtn>
              </form>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageWrapper>
      <Footer />
    </>
  );
};

export default Career;
